import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, PrimaryBtn, inputCls } from "@/components/Common";
import { errMsg, http, inr } from "@/lib/api";

const T = "text", N = "number", L = "list", B = "bool", R = "region", I = "itinerary", D = "dtype";
export const SCHEMAS = {
  packages: { cols: ["name", "region", "days", "price"], fields: [["name", T], ["slug", T], ["region", R], ["location", T], ["country", T], ["days", N], ["price", N], ["image", T], ["summary", T], ["highlights", L], ["itinerary", I]] },
  hotels: { cols: ["name", "city", "stars", "price_per_night"], fields: [["name", T], ["city", T], ["country", T], ["region", R], ["stars", N], ["price_per_night", N], ["rating", N], ["amenities", L], ["image", T], ["free_cancellation", B]] },
  flights: { cols: ["flight_no", "from_code", "to_code", "depart_time", "price"], fields: [["airline", T], ["flight_no", T], ["from_code", T], ["from_city", T], ["to_code", T], ["to_city", T], ["region", R], ["depart_time", T], ["duration_min", N], ["stops", N], ["price", N], ["baggage", T]] },
  coupons: { cols: ["code", "description", "discount_type", "value", "applies_to", "valid_to", "active", "used_count"], fields: [["code", T], ["description", T], ["discount_type", D], ["value", N], ["max_discount", N], ["min_amount", N], ["applies_to", L], ["valid_to", T], ["active", B], ["featured", B]] },
  cabs: { cols: ["name", "seats", "rate_per_km", "airport_fare", "local_fare"], fields: [["name", T], ["model", T], ["seats", N], ["rate_per_km", N], ["airport_fare", N], ["local_fare", N]] },
};
const MONEY = new Set(["price", "price_per_night", "airport_fare", "local_fare"]);

const toForm = (item, fields) => Object.fromEntries(fields.map(([k, t]) => {
  const v = item[k];
  if (t === L) return [k, (v || []).join(", ")];
  if (t === I) return [k, (v || []).map((d) => `${d.title}: ${d.description}`).join("\n")];
  if (t === B) return [k, !!v];
  return [k, v ?? (t === R ? "domestic" : t === D ? "percent" : "")];
}));

const fromForm = (form, fields) => Object.fromEntries(fields.map(([k, t]) => {
  const v = form[k];
  if (t === N) return [k, Number(v) || 0];
  if (t === L) return [k, String(v).split(",").map((s) => s.trim()).filter(Boolean)];
  if (t === I) return [k, String(v).split("\n").filter((l) => l.trim()).map((l, i) => {
    const [title, ...rest] = l.split(":");
    return { day: i + 1, title: title.trim(), description: rest.join(":").trim() };
  })];
  return [k, v];
}));

const Editor = ({ kind, item, onClose, onSaved }) => {
  const { fields } = SCHEMAS[kind];
  const [form, setForm] = useState(toForm(item?.id ? item : kind === "coupons" ? { active: true } : {}, fields));
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = fromForm(form, fields);
      if (item?.id) await http.put(`/admin/items/${kind}/${item.id}`, body);
      else await http.post(`/admin/items/${kind}`, body);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="admin-editor-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{item?.id ? "Edit" : "Add"} {kind.slice(0, -1)}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          {fields.map(([k, t]) => (
            <div key={k} className={t === I || k === "summary" || k === "image" ? "sm:col-span-2" : ""}>
              <Field label={k.replace(/_/g, " ") + (t === L ? " (comma separated" + (kind === "coupons" && k === "applies_to" ? ": package, flight, hotel, cab — empty = all" : "") + ")" : t === I ? " (one day per line — Title: description)" : "")}>
                {t === D ? (
                  <select className={inputCls} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-field-${k}`}>
                    <option value="percent">percent</option><option value="flat">flat (₹)</option>
                  </select>
                ) : t === R ? (
                  <select className={inputCls} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-field-${k}`}>
                    <option value="domestic">domestic</option><option value="international">international</option>
                  </select>
                ) : t === B ? (
                  <input type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} data-testid={`admin-field-${k}`} />
                ) : t === I ? (
                  <textarea rows={7} className={`${inputCls} h-auto py-2`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-field-${k}`} />
                ) : (
                  <input type={t === N ? "number" : k === "valid_to" ? "date" : "text"} step="any" required={k === "name"} className={inputCls} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-field-${k}`} />
                )}
              </Field>
            </div>
          ))}
          <PrimaryBtn type="submit" className="sm:col-span-2" data-testid="admin-editor-save">Save</PrimaryBtn>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const InventoryTable = ({ kind }) => {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState("");
  const { data = [] } = useQuery({ queryKey: ["admin", kind], queryFn: () => http.get(`/admin/items/${kind}`).then((r) => r.data) });
  const { cols } = SCHEMAS[kind];
  const reload = () => { qc.invalidateQueries(); setEdit(null); };
  const shown = data.filter((r) => !filter || JSON.stringify(cols.map((c) => r[c])).toLowerCase().includes(filter.toLowerCase()));
  const del = async (r) => {
    if (!window.confirm("Delete this item?")) return;
    await http.delete(`/admin/items/${kind}/${r.id}`);
    toast.success("Deleted");
    reload();
  };
  return (
    <div data-testid={`admin-inventory-${kind}`}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <input className={`${inputCls} max-w-xs`} placeholder="Search…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid={`admin-search-${kind}`} />
        <PrimaryBtn className="h-10" onClick={() => setEdit({})} data-testid={`admin-add-${kind}`}><Plus className="h-4 w-4" />Add</PrimaryBtn>
      </div>
      <div className="overflow-x-auto rounded-xl border border-[#E5E0D8] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F0EA] text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>{cols.map((c) => <th key={c} className="px-4 py-3">{c.replace(/_/g, " ")}</th>)}<th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {shown.slice(0, 300).map((r) => (
              <tr key={r.id} className="border-t border-[#E5E0D8]" data-testid={`admin-row-${kind}-${r.id}`}>
                {cols.map((c) => <td key={c} className="px-4 py-3">{MONEY.has(c) ? inr(r[c]) : Array.isArray(r[c]) ? (r[c].join(", ") || "all") : String(r[c] ?? "")}</td>)}
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <button onClick={() => setEdit(r)} className="mr-3 text-slate-500 hover:text-[#0D1B2A]" data-testid={`admin-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(r)} className="text-slate-500 hover:text-red-600" data-testid={`admin-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <Editor kind={kind} item={edit} onClose={() => setEdit(null)} onSaved={reload} />}
    </div>
  );
};
